/**
 * MP3Encoder — main-thread façade for the MP3 Web Worker.
 *
 * The real encoder (lamejs, 156KB) runs inside a Web Worker so encoding a 10-minute
 * file never blocks the UI. This module:
 *   - Spawns the worker lazily on first encode() call
 *   - Converts AudioBuffer/Blob → per-channel Float32Array + transfers them zero-copy
 *   - Applies pre-encode fixes that must happen on the main thread: multi-channel downmix,
 *     sample-rate snap to a lamejs-supported rate, bitrate snap to MP3 standard values
 *   - Exposes telemetry about which source (VPS static / VPS CDN proxy / direct jsdelivr)
 *     loaded lamejs
 *
 * Usage:
 *   const mp3Blob = await MP3Encoder.encode(audioBufferOrWavBlob, {
 *       bitrate: 192,          // kbps (CBR). Ignored if vbrQuality is set.
 *       vbrQuality: undefined, // 0..9 (0=highest). Enables VBR mode.
 *       slug: 'pitch',
 *       ownStream: false,      // true — these frames are spliced into the person's OWN stream
 *                              // (mp3-frame-trim.js fades): the plan's quality ceiling does
 *                              // not apply, see __qualityTier.clamp(). Nobody else may claim it.
 *       bare: false            // true — frames only, for a caller that splices them into
 *                              // an existing stream (mp3-frame-trim.js): no source tags
 *                              // patched in, and no `mp3_encoded` event — that event
 *                              // counts exports, and this is a piece of one
 *   });
 */
(function(){
'use strict';

var WORKER_URL = (typeof window!=='undefined'&&window.__jsSrc?window.__jsSrc('/js/mp3-encoder-worker.js'):'/js/mp3-encoder-worker.js');
var _worker = null;
var _workerId = 0;
var _pending = new Map();
var _loadedSource = null;
var _watchdogTimer = null;

// Heartbeat watchdog (2026-05-07). Why this exists:
// On low-RAM Android (kocevnik05@mail.ru, 4 GB / Opera / 11 MB MP3) the OS
// low-memory killer sometimes zombiefies the lamejs Worker mid-encode
// WITHOUT firing onerror. Without the watchdog the Promise hangs forever,
// no `tool_error` event reaches telemetry, the user sees "stuck at 65%
// then reset", and the feedback widget submits with `last_error: null` —
// totally invisible to us.
//
// Heartbeat = ANY message from the worker. The worker emits a dedicated
// `heartbeat` message every HEARTBEAT_MS of wall-clock time while encoding
// (see mp3-encoder-worker.js). It does NOT rely on `progress` cadence:
// progress is emitted per 5 % of work, and on a large file / slow device a
// single 5 % step can exceed this 30 s window. Tying liveness to progress %
// caused 5 false `mp3_worker_hung` on 2026-05-15 (join / audio-compressor,
// 5–54 MB MP3 on contended devices) — fixed by the worker's time-based
// heartbeat. 30 s of true wall-clock silence still = a real zombie worker.
//
// Background-tab caveat: a hidden tab throttles both the worker and this
// timer; silence accrued while `document.hidden` is NOT counted (see the
// visibilitychange handler below) so backgrounding never false-fires.
var WORKER_HEARTBEAT_TIMEOUT_MS = 30000;
var WATCHDOG_TICK_MS = 5000;
// Bounded hidden-grace: bug #609 (Svetlana 2026-06-02 + 51-session 14d cohort)
// — when a Chrome Android PWA tab is backgrounded indefinitely the prior code
// `if (document.hidden || _hiddenAt) return;` paused the watchdog forever, so
// the worker could be zombied by the Android low-memory-killer and we never
// surfaced the hang. The fix: forgive up to STREAM_HIDDEN_GRACE_MS of hidden
// time per job, then declare hung anyway. 30 s matches the foreground timeout
// so a backgrounded tab is judged on the same wall-clock budget as a visible
// one, but we still don't false-fire for casual tab-switches under that cap.
var STREAM_HIDDEN_GRACE_MS = 30000;

// Test hooks — Playwright drives the watchdog with sub-second timeouts via
// these globals; production never sets them. Read fresh per-call so a test
// can flip them on the fly.
function getHeartbeatTimeoutMs(){
    return Number(window.__MP3_WORKER_TIMEOUT_OVERRIDE) || WORKER_HEARTBEAT_TIMEOUT_MS;
}
function getWatchdogTickMs(){
    return Number(window.__MP3_WATCHDOG_TICK_OVERRIDE) || WATCHDOG_TICK_MS;
}
function getHiddenGraceMs(){
    return Number(window.__MP3_HIDDEN_GRACE_OVERRIDE) || STREAM_HIDDEN_GRACE_MS;
}
function getWorkerUrl(){
    return window.__MP3_WORKER_URL_OVERRIDE || WORKER_URL;
}

// Background-tab credit: a hidden tab throttles the worker (and this timer),
// so silence accrued while hidden is not evidence of a hang — for a SHORT
// span. We cap the credit at STREAM_HIDDEN_GRACE_MS per pending job so a tab
// backgrounded for hours (Android PWA + LMK) eventually surfaces the hang.
var _hiddenAt = 0;
// `_hiddenStartByPending` tracks the SUM of hidden time already credited per
// pending job; once it exceeds the grace cap, the watchdog stops crediting
// further hidden spans for that job and declares it hung if it still hasn't
// heartbeat'd.
var _hiddenCreditedMs = new Map();
if (typeof document !== 'undefined' && typeof document.addEventListener === 'function') {
    document.addEventListener('visibilitychange', function(){
        if (document.hidden) {
            _hiddenAt = Date.now();
        } else if (_hiddenAt) {
            var hiddenMs = Date.now() - _hiddenAt;
            _hiddenAt = 0;
            var graceCap = getHiddenGraceMs();
            _pending.forEach(function(p, id){
                if (!p.lastHeartbeat) return;
                var alreadyCredited = _hiddenCreditedMs.get(id) || 0;
                var remainingCap = Math.max(0, graceCap - alreadyCredited);
                var creditNow = Math.min(hiddenMs, remainingCap);
                p.lastHeartbeat += creditNow;
                _hiddenCreditedMs.set(id, alreadyCredited + creditNow);
            });
        }
    });
}

function startWatchdog(){
    if (_watchdogTimer || _pending.size === 0) return;
    _watchdogTimer = setInterval(function(){
        if (_pending.size === 0) {
            clearInterval(_watchdogTimer);
            _watchdogTimer = null;
            return;
        }
        // While currently hidden, decide whether the grace cap is already
        // exhausted. If so, the watchdog ticks even though the page is
        // hidden — Android LMK and PWA backgrounding can zombie the worker
        // indefinitely, so we must surface the hang eventually.
        if (typeof document !== 'undefined' && document.hidden) {
            var graceCap = getHiddenGraceMs();
            var currentHiddenMs = _hiddenAt ? (Date.now() - _hiddenAt) : 0;
            var anyWithinGrace = false;
            _pending.forEach(function(p, id){
                var alreadyCredited = _hiddenCreditedMs.get(id) || 0;
                if (alreadyCredited + currentHiddenMs < graceCap) anyWithinGrace = true;
            });
            if (anyWithinGrace) return;
            // else: fall through and let the watchdog evaluate. Note we do
            // NOT mutate lastHeartbeat here — that would mask the hang.
        }
        var now = Date.now();
        var timeoutMs = getHeartbeatTimeoutMs();
        var hung = [];
        var newestBeat = 0;
        _pending.forEach(function(p, id){
            if (p.lastHeartbeat && p.lastHeartbeat > newestBeat) newestBeat = p.lastHeartbeat;
            if (p.lastHeartbeat && (now - p.lastHeartbeat) > timeoutMs) {
                hung.push({ id: id, lagMs: now - p.lastHeartbeat });
            }
        });
        if (hung.length === 0) return;

        /* ⚠️ Признак жизни — ПО ВОРКЕРУ, а не по заданию. Та же починка, что
           в общем public/js/worker-watchdog.js (2026-08-24); здесь она нужна
           отдельно, потому что этот файл — ВТОРАЯ ДВЕРЬ: общий модуль когда-то
           «извлекли отсюда», а оригинал так и остался со своей копией логики.
           Воркер однопоточный: пока он молотит задание A, задание B стоит в
           очереди и физически не может стучать, а секундомер ему завели при
           постановке в очередь. Прежнее правило убивало живой воркер и
           отклоняло здоровое A с «peer job hung». Любое сообщение из воркера
           доказывает, что он жив, — значит убивать нечего. */
        if (now - newestBeat <= timeoutMs) {
            // Потолок удержания — тот же, что в общем модуле: живой воркер не
            // должен превращаться в вечный спиннер, если он вправду забыл про
            // задание. Молчание дольше 6× таймаута отклоняет ОДНО это задание,
            // не трогая воркер и соседей.
            var stuck = hung.filter(function(h){ return h.lagMs > timeoutMs * 6; });
            try {
                if (typeof window.__ae === 'function') {
                    window.__ae('feature_use', { f: 'worker_watchdog_alive_hold', name: 'mp3-encoder',
                        silent_jobs: hung.length, worst_lag_ms: hung[0].lagMs,
                        newest_beat_ms: now - newestBeat, pending_jobs: _pending.size,
                        stuck_jobs: stuck.length });
                }
            } catch(_){}
            stuck.forEach(function(h){
                var p = _pending.get(h.id);
                if (!p) return;
                _pending.delete(h.id);
                _hiddenCreditedMs.delete(h.id);
                var err = new Error('mp3-encoder: job abandoned by a live worker (' + h.lagMs + 'ms no heartbeat)');
                err.code = 'WORKER_HUNG';
                err.heartbeatLagMs = h.lagMs;
                try { p.reject(err); } catch(_){}
            });
            return;
        }

        // Worker stopped responding — terminate, reject all pending with a
        // typed error, fresh worker spins up on next encode() call.
        try { if (_worker) _worker.terminate(); } catch(_){}
        _worker = null;

        // Telemetry — exactly the silent-hang signal we previously couldn't
        // see. tool_error sets __LAST_TOOL_ERROR so the feedback widget
        // carries it forward, and analytics_events.tool_error is the
        // dashboard's main error stream.
        try {
            if (typeof window.__ae === 'function') {
                window.__ae('tool_error', {
                    error_type: 'mp3_worker_hung',
                    msg: 'mp3-encoder: worker silent for ' + hung[0].lagMs + 'ms',
                    lag_ms: hung[0].lagMs,
                    pending_jobs: _pending.size,
                });
            }
        } catch(_){}

        var hungIds = new Set(hung.map(function(h){ return h.id; }));
        _pending.forEach(function(p, id){
            var info = hungIds.has(id) ? hung.find(function(h){ return h.id === id; }) : null;
            var err = new Error(info
                ? 'mp3-encoder: worker hung (' + info.lagMs + 'ms no heartbeat)'
                : 'mp3-encoder: worker terminated (peer job hung)'
            );
            err.code = 'WORKER_HUNG';
            err.heartbeatLagMs = info ? info.lagMs : null;
            try { p.reject(err); } catch(_){}
        });
        _pending.clear();
        _hiddenCreditedMs.clear();

        clearInterval(_watchdogTimer);
        _watchdogTimer = null;
    }, getWatchdogTickMs());
}

function getWorker(){
    if (_worker) return _worker;
    _worker = new Worker(getWorkerUrl());
    _worker.onmessage = function(ev){
        var msg = ev.data;
        var p = _pending.get(msg.id);
        if (!p) return;
        // ANY message resets the heartbeat — progress, done, error.
        p.lastHeartbeat = Date.now();
        if (msg.type === 'progress' && p.onProgress) { p.onProgress(msg.pct); return; }
        if (msg.type === 'done') {
            _loadedSource = msg.source || _loadedSource;
            _pending.delete(msg.id);
            p.resolve({ mp3: msg.mp3, source: msg.source });
            return;
        }
        if (msg.type === 'error') {
            _pending.delete(msg.id);
            var e0 = new Error(msg.error || 'worker error');
            if (msg.code) { e0.code = msg.code; noteSilentOutput(msg); }
            p.reject(e0);
            return;
        }
        // Streaming protocol — multi-step session keyed by msg.id.
        if (msg.type === 'stream-ready') {
            _loadedSource = msg.source || _loadedSource;
            if (p.onReady) p.onReady();
            return;
        }
        if (msg.type === 'stream-chunk-out') {
            if (p.onChunkOut) p.onChunkOut(msg.mp3, msg.samplesIn, msg.pcm);
            return;
        }
        if (msg.type === 'stream-done') {
            _loadedSource = msg.source || _loadedSource;
            _pending.delete(msg.id);
            if (p.onDone) p.onDone(msg.mp3, msg.source);
            return;
        }
        if (msg.type === 'stream-aborted') {
            _pending.delete(msg.id);
            if (p.onAborted) p.onAborted();
            return;
        }
        if (msg.type === 'stream-error') {
            _pending.delete(msg.id);
            var err = new Error('mp3-encoder: ' + (msg.error || 'stream error'));
            err.code = msg.code || 'STREAM_ERROR';
            if (msg.code) noteSilentOutput(msg);
            if (p.onError) p.onError(err);
            else if (p.reject) try { p.reject(err); } catch(_){}
            return;
        }
    };
    _worker.onerror = function(err){
        // Fatal worker crash — script-level uncaught exception fires here.
        // Android-OOM zombiefication does NOT fire onerror — that's why
        // the watchdog above is the real safety net for that case.
        _pending.forEach(function(p){
            var e = new Error('mp3-encoder: worker crashed: ' + (err.message || err));
            e.code = 'WORKER_CRASH';
            try { p.reject(e); } catch(_){}
        });
        _pending.clear();
        try { _worker.terminate(); } catch(_){}
        _worker = null;
    };
    return _worker;
}

/* The worker read its own result and found frames with no audio in them for input that was loud
   (MP3_SILENT_OUTPUT, mp3-encoder-worker.js). The caller gets the error; this is the line that makes
   the class visible — for three weeks 111 such files left as successes and telemetry showed nothing. */
function noteSilentOutput(msg){
    if (msg.code !== 'MP3_SILENT_OUTPUT') return;
    try {
        if (typeof window.__ae === 'function') {
            var m = msg.meter || {};
            window.__ae('tool_error', {
                error_type: 'mp3_silent_output', msg: 'mp3-encoder: ' + (msg.error || 'silent output'),
                sampleRate: msg.sampleRate, bitrate: msg.bitrate, channels: msg.channels,
                frames: m.frames, full: m.full, blocks: m.blocks, loud: m.loud, src: _loadedSource
            });
        }
    } catch(_){}
}

function report(slug, action, payload){
    try {
        if (typeof window.__ae === 'function') {
            window.__ae('feature_use', Object.assign({ slug: slug || 'unknown', action: action }, payload || {}));
        }
    } catch(_){}
}

async function toAudioBuffer(input){
    if (input && (typeof input.getChannelData === 'function' || typeof input.copyFromChannel === 'function')) return input;
    if (input instanceof Blob) {
        var ctx = new (window.AudioContext || window.webkitAudioContext)();
        try { return await ctx.decodeAudioData(await input.arrayBuffer()); }
        finally { try { ctx.close(); } catch(_){} }
    }
    throw new Error('MP3Encoder.encode: expected AudioBuffer or Blob, got ' + (typeof input));
}

/* Samples [from, from + dst.length) of channel c into dst — the one read every lane of encode()
   goes through. A real AudioBuffer copies natively; an AudioBuffer-shaped VIEW (AudioPost.mixView)
   computes the range it is asked for and has no getChannelData at all, because a whole channel of
   it exists nowhere — which is the point (Sentry TIMBRICA-FRONTEND-NA, 30.09.2026). */
function readInto(src, c, dst, from){
    if (typeof src.copyFromChannel === 'function') { src.copyFromChannel(dst, c, from); return; }
    dst.set(src.getChannelData(c).subarray(from, from + dst.length));
}
function wholeChannel(src, c){
    var a = new Float32Array(src.length);
    readInto(src, c, a, 0);
    return a;
}
// An OfflineAudioContext plays only a real AudioBuffer. Views and shims reach these two rare
// conversions (> 2 channels, a rate lamejs does not take) whole — the old cost, never the common one.
function realBuffer(src){
    if (typeof AudioBuffer === 'function' && src instanceof AudioBuffer) return src;
    var b = new AudioBuffer({ numberOfChannels: src.numberOfChannels, length: src.length, sampleRate: src.sampleRate });
    for (var c = 0; c < src.numberOfChannels; c++) readInto(src, c, b.getChannelData(c), 0);
    return b;
}

// MP3 supports mono/stereo only. Downmix >2ch to stereo via OfflineAudioContext with
// destinationChannelCount=2 — standard BS.775-3 downmix, identical to FFmpeg `-ac 2`.
async function downmixToStereo(buffer){
    if (buffer.numberOfChannels <= 2) return buffer;
    buffer = realBuffer(buffer);
    var OC = window.OfflineAudioContext || window.webkitOfflineAudioContext;
    var oc = new OC({ numberOfChannels: 2, length: buffer.length, sampleRate: buffer.sampleRate });
    var src = oc.createBufferSource();
    src.buffer = buffer;
    src.connect(oc.destination);
    src.start();
    return await oc.startRendering();
}

// lamejs supports specific sample rates. If source rate is already supported, keep it
// (preserves 48kHz from YouTube rips etc. without a lossy resample).
var LAME_RATES = [8000, 11025, 12000, 16000, 22050, 24000, 32000, 44100, 48000];
function nearestLameRate(sr){
    if (LAME_RATES.indexOf(sr) !== -1) return sr;
    var best = 44100, bestDist = Infinity;
    for (var i = 0; i < LAME_RATES.length; i++) {
        var d = Math.abs(LAME_RATES[i] - sr);
        if (d < bestDist) { bestDist = d; best = LAME_RATES[i]; }
    }
    return best;
}
async function resampleForLame(buffer){
    var target = nearestLameRate(buffer.sampleRate);
    if (buffer.sampleRate === target) return buffer;
    buffer = realBuffer(buffer);
    var OC = window.OfflineAudioContext || window.webkitOfflineAudioContext;
    var oc = new OC(buffer.numberOfChannels, Math.ceil(buffer.duration * target), target);
    var src = oc.createBufferSource();
    src.buffer = buffer;
    src.connect(oc.destination);
    src.start();
    return await oc.startRendering();
}

var MP3_BITRATES = [32, 40, 48, 56, 64, 80, 96, 112, 128, 160, 192, 224, 256, 320];
function snapBitrate(b, opts){
    b = parseInt(b) || 192;
    /* The paid quality axis, on the door that actually serves MP3.
     *
     * Most audio tools do NOT encode mp3 with FFmpeg — they use lamejs through
     * this module (ogg/flac/m4a go to FFmpeg, mp3 and wav stay in the browser).
     * So the ceiling applied at the FFmpeg doors missed the format people ask
     * for most: measured 2026-09-18 on /equalizer and /reverb-echo, an
     * anonymous export of a 194 kbps source came back at 192 while the page
     * said "your plan exports 128 kbps".
     *
     * Premium passes through untouched, and a tier snapshot that has not landed
     * yet never clamps — same fail-open rule as every other door. */
    try {
        if (typeof window !== 'undefined' && window.__qualityTier && typeof window.__qualityTier.clamp === 'function') {
            // opts.paid — выдача, оплаченная токенами; opts.ownStream — вставка в собственный
            // поток человека: оба решает clamp(), см. комментарии там.
            var capped = window.__qualityTier.clamp('kbps', b, opts);
            if (capped < b) {
                b = capped;
                if (typeof window.__qualityTier.noteCeilingApplied === 'function') window.__qualityTier.noteCeilingApplied('kbps');
            }
        }
    } catch (_) {}
    /* MP3 has a grid of bitrates, and a request off it has to land on a step. NEAREST put a
       recording that averages 85 kbps on 80 — under what the person brought («Исходное · 85 kbps»
       on /reverb-echo, 05.10.2026) — and for a variable-bitrate file the average is already the
       floor of what CBR needs to say the same. So: the step below only when the request is within
       4 % of it (a CBR file measured by size ÷ duration reads a little over its step — tags, the
       Xing frame), otherwise the step up. */
    var best = MP3_BITRATES[MP3_BITRATES.length - 1];
    for (var i = 0; i < MP3_BITRATES.length; i++) {
        if (MP3_BITRATES[i] >= b) {
            best = (i > 0 && b <= MP3_BITRATES[i - 1] * 1.04) ? MP3_BITRATES[i - 1] : MP3_BITRATES[i];
            break;
        }
    }
    return best;
}

function encodeViaWorker(channels, sampleRate, bitrate, vbrQuality, onProgress, signal){
    return new Promise(function(resolve, reject){
        var id = ++_workerId;
        // Pre-cancellation check — if signal already aborted, reject before
        // we even touch the worker (no wasted postMessage / heartbeat clock).
        if (signal && signal.aborted) {
            reject(new DOMException(
                (typeof signal.reason === 'string' && signal.reason) || 'MP3Encoder.encode: aborted',
                'AbortError'
            ));
            return;
        }
        var p = {
            resolve: resolve,
            reject: reject,
            onProgress: onProgress,
            // Watchdog: rejected if no heartbeat for getHeartbeatTimeoutMs().
            lastHeartbeat: Date.now(),
        };
        _pending.set(id, p);
        var worker = getWorker();
        // Mid-flight cancellation: subscribe to signal. On abort, post
        // stream-abort to the worker (releases its lamejs state, resumes
        // accepting new jobs immediately) and reject the pending promise.
        // Using 'stream-abort' message: even though encode/done isn't a
        // streaming session, the worker handler does delete _streams[id]
        // — harmless for whole-buffer encode (no _streams entry exists,
        // it just no-ops). What we get is the heartbeat watchdog cleanup
        // via the _pending.delete() below.
        var onAbort = null;
        if (signal) {
            onAbort = function () {
                if (!_pending.has(id)) return;  // already resolved
                _pending.delete(id);
                try { worker.postMessage({ id: id, type: 'stream-abort' }); } catch (_) {}
                reject(new DOMException(
                    (typeof signal.reason === 'string' && signal.reason) || 'MP3Encoder.encode: aborted',
                    'AbortError'
                ));
            };
            signal.addEventListener('abort', onAbort, { once: true });
            // Clean up the listener once the promise settles (success or non-abort fail).
            var origResolve = p.resolve, origReject = p.reject;
            p.resolve = function (v) { try { signal.removeEventListener('abort', onAbort); } catch (_) {} origResolve(v); };
            p.reject  = function (e) { try { signal.removeEventListener('abort', onAbort); } catch (_) {} origReject(e); };
        }
        // Transfer the channel buffers — zero-copy hand-off.
        var transfers = channels.map(function(c){ return c.buffer; });
        worker.postMessage({
            id: id,
            type: 'encode',
            channels: channels,
            sampleRate: sampleRate,
            bitrate: bitrate,
            vbrQuality: vbrQuality
        }, transfers);
        startWatchdog();
    });
}

/* encode()'s own lane: the buffer goes to the worker in pieces through ONE stream session, read
   straight into the arrays the worker hands back (takePcm) — the page never holds a second copy of
   the audio, and an AudioBuffer-shaped view is never materialised at all.
   It used to copy every channel whole first: 2 × 45 MB for a 4-minute song at 48 kHz, one
   contiguous block each. A Windows machine with 2 GB had no such block left after the separation
   on /vocal-remover and could not save the minus-track in any format, ten tries in two minutes
   (Sentry TIMBRICA-FRONTEND-NA, 30.09.2026).
   A piece is a whole number of the worker's 1152-sample blocks, so lame is handed exactly the
   blocks of a whole-buffer encode and the MP3 comes out byte for byte the same
   (tests/js/mp3-encoder-encode-in-pieces.test.mjs); 294 912 samples ≈ 6 s at 48 kHz keeps the
   first pieces far inside the stream's 15-s call deadline on a slow machine. */
var PIECE_SAMPLES = 1152 * 256;
// `__MP3_PIECE_SAMPLES` lets a test drive several pieces through a short signal (the real lamejs
// runs below real time under node) — same seam as __OUTPUT_SINK_SPILL_AT. Whole blocks only.
function pieceSamples(){
    var o = Number(typeof window !== 'undefined' && window.__MP3_PIECE_SAMPLES);
    return (o > 0 && o % 1152 === 0) ? o : PIECE_SAMPLES;
}

function abortErrorFor(signal){
    return new DOMException(
        (signal && typeof signal.reason === 'string' && signal.reason) || 'MP3Encoder.encode: aborted',
        'AbortError'
    );
}

async function encodeInPieces(abuf, bitrate, vbrQuality, opts, signal){
    var nCh = abuf.numberOfChannels, len = abuf.length;
    var ctrl = await encodeStream({
        sampleRate: abuf.sampleRate, channels: nCh,
        bitrate: bitrate, vbrQuality: (typeof vbrQuality === 'number') ? vbrQuality : undefined,
        slug: opts.slug, paid: opts.paid === true, ownStream: opts.ownStream === true,
        accumulate: false    // the frames are collected here, as Blob parts — never concatenated
    });
    var parts = [], stop = null, PIECE = pieceSamples();
    if (signal) {
        stop = function(){ try { ctrl.abort(); } catch(_){} };
        signal.addEventListener('abort', stop, { once: true });
    }
    try {
        for (var pos = 0; pos < len; pos += PIECE) {
            if (signal && signal.aborted) throw abortErrorFor(signal);
            var n = Math.min(PIECE, len - pos), back = ctrl.takePcm(), piece = [];
            for (var c = 0; c < nCh; c++) {
                var a = (back && back[c] && back[c].length === n) ? back[c] : new Float32Array(n);
                readInto(abuf, c, a, pos);
                piece.push(a);
            }
            var bytes = await ctrl.feed(piece);
            if (bytes && bytes.byteLength) parts.push(new Uint8Array(bytes));
            if (opts.onProgress) { try { opts.onProgress((pos + n) / len); } catch(_){} }
        }
        if (signal && signal.aborted) throw abortErrorFor(signal);
        var done = await ctrl.finish();
        if (done && done.tail && done.tail.byteLength) parts.push(new Uint8Array(done.tail));
        return { parts: parts, head: done && done.head, source: done && done.source, lane: 'pieces' };
    } catch (err) {
        try { ctrl.abort(); } catch(_){}
        if (signal && signal.aborted) throw abortErrorFor(signal);
        throw err;
    } finally {
        if (stop) { try { signal.removeEventListener('abort', stop); } catch(_){} }
    }
}

/* ── Where the music starts: the encoder's own delay, declared (28.10.2026) ──────────────────
   LAME puts 576 samples of its own in front of the music and every decoder 529 more; the last
   frame runs past the end to a whole frame. LAME itself, and libmp3lame in our warm FFmpeg lane
   (AudioPipeline.encodeMP3), say so in an Info frame — encoder delay and padding in its LAME tag —
   and decoders cut both. lamejs wrote nothing, so every MP3 of this lane began 1105 samples
   (23–69 ms) late and ran up to two frames long, while the same export through the FFmpeg lane
   did not: which lane a file took decided whether it lined up with its source (stems of
   /remove-word-from-song in a DAW, a song re-uploaded for a second edit, a cut against the
   original). Measured on a song, 8 rate × bitrate × channel cases (44.1/48/32/24/22.05/16 kHz,
   stereo and mono): ffmpeg, Chromium, Chrome and Firefox decode the tagged file at lag 0 to the
   exact sample count — as they decode libmp3lame's. Media Foundation (the Windows players) reads
   no tag and decodes the tag frame as one frame of silence, exactly as it does every LAME file
   and our FFmpeg lane. The frame is the stream's own CBR frame («Info»), so nothing that parses
   MPEG audio meets anything new. ⚠️ Our OWN delay only: a tag carrying a SOURCE's delay in front
   of spliced frames cuts real audio (docs/why/primitives/mp3-lossless-splice.md) — `bare` frames
   never get one. Fail-open: a stream this cannot walk leaves as before, untagged. */
var LAME_ENC_DELAY = 576;
var LAME_TAG_VERSION = 'LAME3.98r';   // what the vendored lamejs (LAME 3.98.4) calls itself
var MPA_KBPS = { 1: [0, 32, 40, 48, 56, 64, 80, 96, 112, 128, 160, 192, 224, 256, 320],
                 2: [0, 8, 16, 24, 32, 40, 48, 56, 64, 80, 96, 112, 128, 144, 160] };
var MPA_RATE = { 3: [44100, 48000, 32000], 2: [22050, 24000, 16000], 0: [11025, 12000, 8000] };
var CRC16 = (function(){
    var t = new Uint16Array(256);
    for (var n = 0; n < 256; n++) {
        var c = n;
        for (var k = 0; k < 8; k++) c = (c & 1) ? ((c >>> 1) ^ 0xA001) : (c >>> 1);
        t[n] = c;
    }
    return t;
})();
/** LAME's CRC-16 (reflected 0x8005, from 0), continued over bytes[from, to). */
function crc16(c, bytes, from, to){
    for (var i = from; i < to; i++) c = (c >>> 8) ^ CRC16[(c ^ bytes[i]) & 0xFF];
    return c & 0xFFFF;
}
function layer3Header(b0, b1, b2, b3){
    if (b0 !== 0xFF || (b1 & 0xE0) !== 0xE0) return null;
    var ver = (b1 >> 3) & 3, layer = (b1 >> 1) & 3;
    if (layer !== 1 || ver === 1) return null;
    var bri = b2 >> 4, sri = (b2 >> 2) & 3;
    if (!bri || bri === 15 || sri === 3) return null;
    var v1 = ver === 3, kbps = MPA_KBPS[v1 ? 1 : 2][bri], sr = MPA_RATE[ver][sri], spf = v1 ? 1152 : 576;
    return { v1: v1, ver: ver, bri: bri, sri: sri, sr: sr, spf: spf, kbps: kbps, mono: ((b3 >> 6) & 3) === 3,
             len: Math.floor(spf / 8 * kbps * 1000 / sr) + ((b2 >> 1) & 1), b1: b1, b2: b2, b3: b3 };
}
/**
 * What the Info frame needs, gathered while the frames go by: one tally per output stream, fed
 * every chunk in order (a frame may straddle two), so a stream written to disk is never read back
 * for it. `bad` — not one clean Layer III stream (the frame is then not written: fail-open).
 */
function layer3Tally(){
    var t = { total: 0, next: 0, hb: [], first: null, offs: [], bad: false, crc: 0 };
    t.feed = function (u) {
        if (!u || !u.length || t.bad) return;
        t.crc = crc16(t.crc, u, 0, u.length);
        var end = t.total + u.length;
        while (t.next + t.hb.length < end) {
            for (var q = t.next + t.hb.length - t.total; q < u.length && t.hb.length < 4; q++) t.hb.push(u[q]);
            if (t.hb.length < 4) break;
            var h = layer3Header(t.hb[0], t.hb[1], t.hb[2], t.hb[3]);
            t.hb = [];
            if (!h || (t.first && (h.ver !== t.first.ver || h.sri !== t.first.sri))) { t.bad = true; break; }
            if (!t.first) t.first = h;
            t.offs.push(t.next);
            t.next += h.len;
        }
        t.total = end;
    };
    /** The Info frame for a stream of `samples` samples per channel, or null. */
    t.frame = function (samples) {
        try {
            if (t.bad || !t.first || t.next !== t.total || !(samples > 0)) return null;
            var h = t.first, frames = t.offs.length;
            var padding = frames * h.spf - LAME_ENC_DELAY - samples;
            if (padding < 0 || padding > 4095) return null;
            var si = h.v1 ? (h.mono ? 17 : 32) : (h.mono ? 9 : 17);
            var lame = 4 + si + 120;                       // Xing: tag, flags, frames, bytes, TOC, quality
            var table = MPA_KBPS[h.v1 ? 1 : 2], bri = h.bri;
            var lenFor = function (i) { return Math.floor(h.spf / 8 * table[i] * 1000 / h.sr); };
            while (bri < 14 && lenFor(bri) < lame + 36) bri++;   // the stream's bitrate; larger only if the tag does not fit
            var len = lenFor(bri);
            if (len < lame + 36) return null;
            var f = new Uint8Array(len), streamBytes = len + t.total, o = 4 + si, i;
            f[0] = 0xFF; f[1] = h.b1 | 0x01; f[2] = (bri << 4) | (h.sri << 2) | (h.b2 & 0x01); f[3] = h.b3;
            var put32 = function (v) { f[o] = v >>> 24 & 255; f[o + 1] = v >>> 16 & 255; f[o + 2] = v >>> 8 & 255; f[o + 3] = v & 255; o += 4; };
            f[o] = 0x49; f[o + 1] = 0x6E; f[o + 2] = 0x66; f[o + 3] = 0x6F; o += 4;   // «Info»: constant bitrate
            put32(0x0F);                                   // frames | bytes | TOC | quality
            put32(frames);                                 // audio frames, not counting this one
            put32(streamBytes);                            // this frame included
            for (i = 0; i < 100; i++) f[o + i] = Math.min(255, Math.floor((len + t.offs[Math.floor(i / 100 * frames)]) / streamBytes * 256));
            o += 100;
            put32(0);                                      // quality: not measured
            for (i = 0; i < 9; i++) f[o + i] = LAME_TAG_VERSION.charCodeAt(i);
            o += 9;
            f[o++] = 1;                                    // revision 0, CBR
            o += 1 + 4 + 2 + 2 + 1;                        // lowpass, peak, two replay gains, flags/ATH: not declared
            f[o++] = Math.min(255, h.kbps);
            f[o++] = (LAME_ENC_DELAY >> 4) & 0xFF;         // delay 12 bits | padding 12 bits
            f[o++] = ((LAME_ENC_DELAY & 0x0F) << 4) | ((padding >> 8) & 0x0F);
            f[o++] = padding & 0xFF;
            f[o++] = h.sr === 44100 ? 0x40 : h.sr === 48000 ? 0x80 : 0x00;   // source rate bits (≤ 32 kHz: 00)
            o += 1 + 2;                                    // mp3 gain, preset/surround
            put32(streamBytes);                            // music length
            f[o++] = t.crc >> 8; f[o++] = t.crc & 255;     // music CRC
            var tag = crc16(0, f, 0, o);                   // the frame up to here: 190 bytes for MPEG-1 stereo
            f[o++] = tag >> 8; f[o++] = tag & 255;
            return f;
        } catch (_) { return null; }
    };
    return t;
}
/**
 * The Info frame (Xing header + LAME tag: encoder delay, padding, CRCs) for a headerless lamejs
 * stream held as parts, `samples` samples per channel — or null when it cannot be written.
 */
function lameInfoFrame(parts, samples){
    var t = layer3Tally();
    for (var i = 0; parts && i < parts.length; i++) t.feed(parts[i]);
    return t.frame(samples);
}

async function encode(input, opts){
    opts = opts || {};
    var slug = opts.slug || null;
    var useVBR = typeof opts.vbrQuality === 'number';
    var bitrate = useVBR ? null : snapBitrate(opts.bitrate, opts);
    var vbrQuality = useVBR ? Math.max(0, Math.min(9, parseInt(opts.vbrQuality))) : null;
    var signal = opts.signal || null;
    if (signal && signal.aborted) {
        throw new DOMException(
            (typeof signal.reason === 'string' && signal.reason) || 'MP3Encoder.encode: aborted',
            'AbortError'
        );
    }

    // Pre-encode fixups on main thread (cheap Web Audio ops that can't easily run in a worker)
    var abuf = await toAudioBuffer(input);
    abuf = await downmixToStereo(abuf);
    abuf = await resampleForLame(abuf);

    var nCh = abuf.numberOfChannels;
    var result = null;
    try {
        result = await encodeInPieces(abuf, bitrate, vbrQuality, opts, signal);
    } catch (err) {
        if (err && err.name === 'AbortError') throw err;
        // The same encoder would write the same silence from the whole buffer: this one is the
        // caller's to answer (AudioPipeline.encodeMP3 goes to FFmpeg), not a lane to retry.
        if (err && err.code === 'MP3_SILENT_OUTPUT') throw err;
        // A stream that would not start or broke half-way (a piece past its deadline on a starved
        // device) is not the end of the export: the whole buffer goes the old way, one message.
        report(slug, 'mp3_stream_fallback', { err: String((err && (err.code || err.message)) || err).substring(0, 80) });
    }
    if (!result) {
        // The old way: every channel copied whole for the worker (a view into AudioBuffer memory
        // cannot be transferred — Safari has had bugs with it). Kept only as the fallback above.
        var channels = [];
        for (var c = 0; c < nCh; c++) channels.push(wholeChannel(abuf, c));
        var one = await encodeViaWorker(channels, abuf.sampleRate, bitrate, vbrQuality, opts.onProgress, signal);
        result = { parts: [new Uint8Array(one.mp3)], source: one.source, lane: 'whole' };
    }

    if (result.source && !_loadedSource) _loadedSource = result.source;

    // Optional metadata preservation — two independent channels:
    //   opts.id3   — ID3v2 at START of file (title/artist/album/…/APIC + custom frames)
    //   opts.id3v1 — ID3v1 at END of file (128 bytes starting with "TAG"), rarely the only
    //                metadata but still read by older players
    //
    // Forms accepted for opts.id3:
    //   { raw: Uint8Array|ArrayBuffer }  → prepend bytes verbatim (best for re-encode,
    //                                       preserves EVERY frame including ones we can't parse)
    //   { title, artist, album, ... }    → synthesize ID3v2.3 via ID3Writer (for tools
    //                                       that invent tags, e.g. generated audio)
    var v2Bytes = null, v1Bytes = null;

    if (opts.id3) {
        try {
            if (opts.id3.raw && (opts.id3.raw instanceof Uint8Array || opts.id3.raw instanceof ArrayBuffer)) {
                v2Bytes = opts.id3.raw instanceof Uint8Array ? opts.id3.raw : new Uint8Array(opts.id3.raw);
            } else if (window.ID3Writer) {
                v2Bytes = window.ID3Writer.build(opts.id3);
            }
        } catch(e) {
            console.warn('[mp3-encoder] ID3v2 attach failed:', e.message);
            report(slug, 'mp3_id3_write_failed', { err: (e.message || '').substring(0, 120) });
        }
    }

    if (opts.id3v1 && (opts.id3v1 instanceof Uint8Array || opts.id3v1 instanceof ArrayBuffer)) {
        var v1 = opts.id3v1 instanceof Uint8Array ? opts.id3v1 : new Uint8Array(opts.id3v1);
        // ID3v1 is always exactly 128 bytes starting with "TAG" — validate before appending
        if (v1.length === 128 && v1[0] === 0x54 && v1[1] === 0x41 && v1[2] === 0x47) v1Bytes = v1;
    }

    // The tags and the frames go into the Blob as parts: the browser joins them, so the file is
    // never one buffer in the page (it used to be merged into one, a second copy of the whole MP3).
    // The Info frame is the first audio frame: after the ID3v2 prefix, before the music.
    var gapless = opts.bare ? null : (result.head || lameInfoFrame(result.parts, abuf.length));
    var blobParts = [];
    if (v2Bytes) blobParts.push(v2Bytes);
    if (gapless) blobParts.push(gapless);
    for (var pi = 0; pi < result.parts.length; pi++) blobParts.push(result.parts[pi]);
    if (v1Bytes) blobParts.push(v1Bytes);
    var blob = new Blob(blobParts, { type: 'audio/mpeg' });
    if (blob.size === 0) throw new Error('empty output');

    if (opts.reportSuccess !== false && !opts.bare) {
        report(slug, 'mp3_encoded', {
            src: _loadedSource,
            bitrate: bitrate,
            vbrQuality: vbrQuality,
            size: blob.size,
            sampleRate: abuf.sampleRate,
            channels: nCh,
            mode: useVBR ? 'vbr' : 'cbr',
            lane: result.lane,
            gapless: gapless ? 1 : 0
        });
    }
    /* Теги источника. lamejs кодирует из PCM и об исходном файле не знает
       ничего, поэтому mp3 отсюда выходил БЕЗ названия и исполнителя — при том
       что тот же тул, отдавший файл в ffmpeg напрямую, теги сохранял (замер
       23.09.2026 на /speed). Политика — общая с ffmpeg-лейном, см.
       audio-source-tags.js; здесь только применение к готовому blob'у.
       Fail-open: осечка переноса не имеет права стоить человеку экспорта. */
    try {
        if (!opts.bare && window.__audioSourceTags) blob = await window.__audioSourceTags.patchBlob(blob, 'out.mp3');
    } catch (_) { }

    return blob;
}

// Preload the worker + lamejs so the first encode is instant.
// Currently we don't need a dedicated preload — calling encode() with a tiny silent buffer would work,
// but that's wasteful. Leave it as a no-op for API parity.
function preload(){ /* reserved */ return Promise.resolve(); }

function terminate(){
    if (_worker) { try { _worker.terminate(); } catch(_){} _worker = null; }
    _pending.forEach(function(p){ p.reject(new Error('terminated')); });
    _pending.clear();
}

/**
 * Streaming MP3 encoder — chunked input → chunked output. Worker holds only
 * one chunk of PCM at a time. Caller pushes audio in pieces, gets back
 * encoded mp3 frames, calls finish() to flush and assemble the final Blob.
 *
 *     const ctrl = await MP3Encoder.encodeStream({
 *         sampleRate: 44100,    // required
 *         channels: 2,          // 1 or 2
 *         bitrate: 128,         // CBR kbps. snapped to nearest valid value
 *         vbrQuality: undefined,// 0..9 — currently routed to legacy encode()
 *         slug: 'volume',
 *     });
 *
 *     await ctrl.feed([leftFloat32, rightFloat32]);  // push chunk
 *     await ctrl.feed([leftFloat32, rightFloat32]);  // ...
 *     const fin = await ctrl.finish();               // flush → { blob, head, samplesIn, source }
 *
 *     // ctrl.abort() if user cancels mid-stream.
 *
 * `head` is the Info frame (the encoder's own delay and padding, see layer3Tally): with the
 * default accumulate it is already the first frame of `blob`. With `accumulate: false` the caller
 * keeps the frames — into an OutputSink — and puts it in front:
 *     OutputSink.prepend(await sink.finish(), fin.head)
 * (tests/js/mp3-encoder-declares-its-delay.test.mjs holds every such caller to it).
 *
 * Memory: O(one chunk's PCM) + O(encoded chunks held until finish). The
 * chunks accumulator is on main thread; disk-backed assembly for very long files is
 * OutputSink (audio-output-sink.js) with `accumulate: false`.
 */
async function encodeStream(opts){
    opts = opts || {};
    var sampleRate = parseInt(opts.sampleRate, 10);
    var channels = (opts.channels === 1) ? 1 : 2;
    var useVBR = typeof opts.vbrQuality === 'number';
    var bitrate = useVBR ? null : snapBitrate(opts.bitrate, opts);
    var vbrQuality = useVBR ? Math.max(0, Math.min(9, parseInt(opts.vbrQuality))) : null;
    // accumulate: true (default) → finish() returns full Blob from collected
    //   chunks. Caller-friendly but holds O(output) bytes in main-thread RAM.
    // accumulate: false → finish() returns just the tail bytes from lamejs
    //   flush. Caller is responsible for collecting feed() returns into
    //   their own sink (e.g. OPFS) — the only path that keeps main-thread
    //   heap O(chunk).
    var accumulate = opts.accumulate !== false;
    if (!sampleRate || sampleRate < 1000) {
        throw new Error('MP3Encoder.encodeStream: invalid sampleRate');
    }
    // Force a sane lamejs sample rate. If caller passes 47999, we still emit
    // at 47999 — but lamejs only supports specific rates, so it'd reject.
    // Caller is expected to pre-resample; we don't transparently resample
    // here (would defeat streaming if buffering for resample).
    if (LAME_RATES.indexOf(sampleRate) === -1) {
        throw new Error('MP3Encoder.encodeStream: lamejs does not support sample rate ' + sampleRate
            + ' (supported: ' + LAME_RATES.join(',') + ')');
    }

    var id = ++_workerId;
    var collected = []; // Uint8Array list, concatenated on finish()
    var totalBytes = 0;
    var samplesIn = 0;
    // The Info frame's facts, gathered as the frames go by — with or without `accumulate`, so a
    // caller writing to disk gets the frame from finish() without reading its file back.
    var tally = layer3Tally();

    // Pending entry tracks the LIVE session: 'feed' resolves on next chunk-out,
    // 'finish' on stream-done, abort fires onAborted.
    var current = null; // null between calls; { resolve, reject } during feed/finish
    var aborted = false;
    var lastPcm = null; // the arrays of the last feed, returned by the worker (takePcm)

    function makeStreamEntry(resolveReady, rejectReady) {
        return {
            // Heartbeat for watchdog (any worker message resets it).
            lastHeartbeat: Date.now(),
            // Multiplexed callbacks — onmessage (in getWorker) routes here.
            onReady: function(){ resolveReady(); },
            onChunkOut: function(mp3Buf, sIn, pcm){
                // the fed arrays, back from the worker for the next piece (controller.takePcm)
                lastPcm = pcm && pcm.length ? pcm : null;
                if (mp3Buf && mp3Buf.byteLength > 0) tally.feed(new Uint8Array(mp3Buf));
                // Only collect if accumulator mode requested.
                if (accumulate && mp3Buf && mp3Buf.byteLength > 0) {
                    collected.push(new Uint8Array(mp3Buf));
                    totalBytes += mp3Buf.byteLength;
                }
                samplesIn = sIn || samplesIn;
                if (current) {
                    var c = current; current = null;
                    c.resolve(mp3Buf);
                }
            },
            onDone: function(tailBuf, source){
                if (tailBuf && tailBuf.byteLength > 0) tally.feed(new Uint8Array(tailBuf));
                // In front of the music, by whoever assembles the file: here when we do (accumulate),
                // the caller's sink otherwise (OutputSink.prepend). See layer3Tally / lameInfoFrame.
                var head = tally.frame(samplesIn);
                if (accumulate) {
                    if (tailBuf && tailBuf.byteLength > 0) {
                        collected.push(new Uint8Array(tailBuf));
                        totalBytes += tailBuf.byteLength;
                    }
                    var out = new Uint8Array(totalBytes);
                    var off = 0;
                    for (var i = 0; i < collected.length; i++) {
                        out.set(collected[i], off); off += collected[i].length;
                    }
                    collected = null;
                    if (current) {
                        var c2 = current; current = null;
                        c2.resolve({ blob: new Blob(head ? [head, out] : [out], { type: 'audio/mpeg' }), source: source, samplesIn: samplesIn, head: head });
                    }
                } else {
                    // Caller is responsible for the bulk; we only return tail.
                    if (current) {
                        var c2b = current; current = null;
                        c2b.resolve({
                            tail: tailBuf,
                            tailBytes: tailBuf ? tailBuf.byteLength : 0,
                            source: source,
                            samplesIn: samplesIn,
                            head: head,
                        });
                    }
                }
            },
            onAborted: function(){
                aborted = true;
                if (current) {
                    var c3 = current; current = null;
                    c3.reject(Object.assign(new Error('MP3Encoder.encodeStream: aborted'), { code: 'ABORTED' }));
                }
            },
            onError: function(err){
                aborted = true;
                if (current) {
                    var c4 = current; current = null;
                    c4.reject(err);
                } else {
                    rejectReady(err);
                }
            },
            // Reserved by watchdog — peer-job-hung messages go through reject.
            reject: function(err){
                aborted = true;
                if (current) {
                    var c5 = current; current = null;
                    c5.reject(err);
                } else {
                    rejectReady(err);
                }
            },
        };
    }

    return new Promise(function(resolveReady, rejectReady){
        var entry = makeStreamEntry(function(){ resolveReady(controller); }, rejectReady);
        _pending.set(id, entry);
        var worker = getWorker();
        worker.postMessage({
            id: id, type: 'stream-start',
            sampleRate: sampleRate, bitrate: bitrate, vbrQuality: vbrQuality,
            channels: channels,
        });
        startWatchdog();

        // Per-call deadline. The heartbeat-watchdog above covers the worker
        // as a WHOLE (silence for >30 s = hung). But individual stages of a
        // streaming session can stall for reasons that DON'T trigger the
        // heartbeat — e.g. the worker is alive and still emits heartbeats
        // for another job's chunk loop, but the stream-chunk-out for THIS
        // job's last chunk got dropped during a backgrounding event. This
        // per-call deadline ensures a single hung feed/finish surfaces as
        // a typed error even when the heartbeat path slips through.
        // Bug #609 (Svetlana, 2026-06-02) — 51-session 14d freeze cohort,
        // 0/51 sessions emitted mp3_worker_hung because the worker DID
        // heartbeat for the global watchdog but THIS job's response never
        // landed. Per-call deadline makes those visible.
        var STREAM_CALL_DEADLINE_MS = 15000;
        // SELF-CALIBRATING DEADLINE. A fixed 15 s is a statement about the
        // MACHINE, not about a hang — and it is wrong on slow ones. Support
        // case 2026-07-25 (Лаура): her browser reported `simd_unsupported`, so
        // the encoder fell back to the no-SIMD single-thread build; encoding one
        // 15 s window there legitimately takes longer than 15 s, and the join
        // died with `mp3_stream_call_timeout` although nothing was hung.
        // Raising the constant for everyone would blind the fast majority to
        // real hangs (bug #609's 51-session freeze cohort), so instead the
        // deadline learns from THIS device: once a few feeds have completed we
        // allow 6× the observed median, floored at the old 15 s and capped at
        // 90 s so a genuine hang still surfaces. Fast devices keep 15 s.
        var _feedTimes = [];
        function deadlineFor(callName){
            var override = Number(window.__MP3_STREAM_CALL_DEADLINE_OVERRIDE);
            if (override) return override;
            return MP3Encoder.__adaptiveDeadlineMs(_feedTimes, STREAM_CALL_DEADLINE_MS);
        }
        // The deadline runs on VISIBLE time (window.__visibleTimeout). A phone that is
        // switched to another app freezes the page TOGETHER with this worker; on a wall
        // clock the timer fired on return, before the frozen worker could answer, and a
        // healthy export died as `mp3_stream_call_timeout` — the class recorded in
        // docs/why/primitives/visible-timeout.md («сторож "воркер молчит N с"»). A
        // minutes-long export (/audio-mixer in windows, /join chunked) is exactly when a
        // person glances at another app. The heartbeat watchdog above keeps its own
        // bounded hidden grace, so a worker that is really gone still surfaces.
        // Returns the cancel function.
        function armDeadline(fn, ms){
            if (typeof window !== 'undefined' && typeof window.__visibleTimeout === 'function') {
                var cancel = window.__visibleTimeout(fn, ms);
                if (typeof cancel === 'function') return cancel;
            }
            var h = setTimeout(fn, ms);
            return function(){ clearTimeout(h); };
        }
        // How long a feed took — on the same clock, so a frozen minute does not
        // teach the adaptive deadline that this device is slow.
        function streamNow(){
            if (typeof window !== 'undefined' && typeof window.__visibleNow === 'function') return window.__visibleNow();
            return (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
        }
        // Abort the session locally + tell the worker to discard its state.
        // After this the encoder is unusable; the caller must spin a new one.
        function abortStreamOnTimeout(stage){
            if (aborted) return;
            aborted = true;
            try { worker.postMessage({ id: id, type: 'stream-abort' }); } catch(_){}
            _pending.delete(id);
            _hiddenCreditedMs.delete(id);
        }

        var controller = {
            samplesIn: function(){ return samplesIn; },
            // The arrays the last feed() sent, handed back by the worker once encoded — refill them
            // with the next piece instead of allocating one: fresh pieces are the whole file allocated
            // again (114 MB for a 5-min stereo master at 48 kHz), freed only as fast as the worker
            // collects (2026-10-01). Null before the first feed, after this call until the next one,
            // and from an older worker.
            takePcm: function(){ var p = lastPcm; lastPcm = null; return p; },
            feed: function(channelArrays){
                if (aborted) return Promise.reject(new Error('MP3Encoder.encodeStream: aborted/closed'));
                if (!channelArrays || channelArrays.length === 0) {
                    return Promise.reject(new Error('MP3Encoder.encodeStream.feed: channels required'));
                }
                return new Promise(function(res, rej){
                    if (current) {
                        rej(new Error('MP3Encoder.encodeStream.feed: previous feed not yet resolved'));
                        return;
                    }
                    var dlMs = deadlineFor('feed');
                    var settled = false;
                    var _t0 = streamNow();
                    var deadlineTimer = armDeadline(function(){
                        if (settled) return;
                        settled = true;
                        if (current && current.__deadlineTimer === deadlineTimer) current = null;
                        abortStreamOnTimeout('feed');
                        try {
                            if (typeof window.__ae === 'function') {
                                window.__ae('tool_error', {
                                    error_type: 'mp3_stream_call_timeout',
                                    call: 'feed', timeout_ms: dlMs, slug: opts.slug || 'unknown',
                                });
                            }
                        } catch(_){}
                        var err = new Error('MP3Encoder.encodeStream.feed: no stream-chunk-out within ' + dlMs + 'ms');
                        err.code = 'STREAM_CALL_TIMEOUT';
                        err.stage = 'feed';
                        rej(err);
                    }, dlMs);
                    current = {
                        __deadlineTimer: deadlineTimer,
                        resolve: function(v){
                            if (settled) return; settled = true; deadlineTimer();
                            // Feed the observed duration back in, so the NEXT window is
                            // judged by how fast THIS device actually encodes.
                            var _now = streamNow();
                            _feedTimes.push(_now - _t0);
                            if (_feedTimes.length > 12) _feedTimes.shift();
                            res(v);
                        },
                        reject: function(e){ if (settled) return; settled = true; deadlineTimer(); rej(e); },
                    };
                    var transfers = [];
                    for (var i = 0; i < channelArrays.length; i++) {
                        if (channelArrays[i] && channelArrays[i].buffer) transfers.push(channelArrays[i].buffer);
                    }
                    worker.postMessage({ id: id, type: 'stream-chunk', channels: channelArrays }, transfers);
                });
            },
            finish: function(){
                if (aborted) return Promise.reject(new Error('MP3Encoder.encodeStream: aborted/closed'));
                return new Promise(function(res, rej){
                    if (current) {
                        rej(new Error('MP3Encoder.encodeStream.finish: pending feed not resolved'));
                        return;
                    }
                    var dlMs = deadlineFor('finish');
                    var settled = false;
                    var deadlineTimer = armDeadline(function(){
                        if (settled) return;
                        settled = true;
                        if (current && current.__deadlineTimer === deadlineTimer) current = null;
                        abortStreamOnTimeout('finish');
                        try {
                            if (typeof window.__ae === 'function') {
                                window.__ae('tool_error', {
                                    error_type: 'mp3_stream_call_timeout',
                                    call: 'finish', timeout_ms: dlMs, slug: opts.slug || 'unknown',
                                });
                            }
                        } catch(_){}
                        var err = new Error('MP3Encoder.encodeStream.finish: no stream-done within ' + dlMs + 'ms');
                        err.code = 'STREAM_CALL_TIMEOUT';
                        err.stage = 'finish';
                        rej(err);
                    }, dlMs);
                    current = {
                        __deadlineTimer: deadlineTimer,
                        resolve: function(v){ if (settled) return; settled = true; deadlineTimer(); res(v); },
                        reject: function(e){ if (settled) return; settled = true; deadlineTimer(); rej(e); },
                    };
                    worker.postMessage({ id: id, type: 'stream-end' });
                });
            },
            abort: function(){
                if (aborted) return;
                aborted = true;
                if (worker) {
                    try { worker.postMessage({ id: id, type: 'stream-abort' }); } catch(_){}
                }
                if (current) {
                    var c = current; current = null;
                    c.reject(Object.assign(new Error('MP3Encoder.encodeStream: aborted'), { code: 'ABORTED' }));
                }
                _pending.delete(id);
            },
            isAborted: function(){ return aborted; },
        };
    });
}

window.MP3Encoder = {
    encode: encode,
    encodeStream: encodeStream,
    lameInfoFrame: lameInfoFrame,
    preload: preload,
    terminate: terminate,
    // What encode() holds at once for an AudioBuffer-shaped input, beyond the input itself — the
    // memory guard in AudioPipeline asks this instead of assuming two whole copies. A piece in
    // flight and its bytes back; whole only where the buffer must first be converted (more than
    // two channels, a rate lamejs does not take), then counted as before.
    peakBytes: function(input){
        if (!input || !(typeof input.getChannelData === 'function' || typeof input.copyFromChannel === 'function')) return 0;
        var ch = input.numberOfChannels || 1;
        if (ch > 2 || LAME_RATES.indexOf(input.sampleRate) === -1) return input.length * ch * 4 * 2;
        return pieceSamples() * ch * 4 * 2;
    },
    // Per-call deadline, derived from how fast THIS device actually encodes.
    // Pure + exported so it can be tested directly: the encoder runs in a Web
    // Worker, and CDP CPU-throttling does NOT slow workers down, so an
    // end-to-end "make the machine slow" repro is impossible in Playwright
    // (measured: even 25× throttling never trips the fixed 15 s). Testing the
    // rule itself is therefore the honest way to guard it.
    //   < 3 samples  → the fixed floor (a fresh session keeps the tight 15 s
    //                  that made bug #609's freeze cohort visible)
    //   otherwise    → 6 × median feed time, floored at `floorMs`, capped at
    //                  90 s so a genuine hang still surfaces.
    __adaptiveDeadlineMs: function(feedTimes, floorMs){
        floorMs = floorMs || 15000;
        if (!feedTimes || feedTimes.length < 3) return floorMs;
        var a = feedTimes.slice().sort(function(x, y){ return x - y; });
        var med = a[Math.floor(a.length / 2)];
        return Math.max(floorMs, Math.min(90000, Math.round(med * 6)));
    },
    // The grid rule above, exported for its test (pure when no tier snapshot is on the page).
    __snapBitrate: snapBitrate,
    loadedFrom: function(){ return _loadedSource; },
    isAvailable: function(){ return !!_worker || !!_loadedSource; },
    engine: 'lamejs (Web Worker, streaming-capable)',
    version: '2.2.0'
};

})();
